import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { ArrowRight, Check, IndianRupee, MessageCircle } from "lucide-react";
import { API, Shell, fmtTime, track, useT } from "./Shell";
import "./testTheme.css";

const Tracker = ({ reward, booking, labels }) => {
  const steps = [
    [labels[0], true],
    [labels[1], !!booking?.consultation_done],
    [labels[2], !!booking?.viva_done],
    [labels[3], ["Approved for Payment", "Payment Completed"].includes(reward?.status)],
    [labels[4], reward?.status === "Payment Completed"],
  ];
  return <div className="tp-track" data-testid="tp-tracker">{steps.map(([label, done], i) => (
    <div key={label} style={{ display: "flex", alignItems: "center", flex: i < steps.length - 1 ? 1 : "0" }}>
      <div className={done ? "node done" : "node"}><div className="dot">{done ? <Check size={15} /> : i + 1}</div><span>{label}</span></div>
      {i < steps.length - 1 && <div className={steps[i + 1][1] ? "link done" : "link"} />}
    </div>
  ))}</div>;
};

export default function TestResult() {
  const { attemptId } = useParams();
  const navigate = useNavigate();
  const T = useT();
  const [payload, setPayload] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    axios.post(`${API}/test/submit`, { attempt_id: attemptId }).catch(() => {})
      .finally(() => axios.get(`${API}/test/result/${attemptId}`)
        .then(({ data }) => setPayload(data))
        .catch(() => setError("load")));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attemptId]);

  if (error) return <Shell><div className="tp-wrap"><div className="tp-hero"><p className="tp-error" data-testid="tp-result-error">{T.r_load_err}</p><a className="tp-btn secondary" href="/physics-test">{T.e_back}</a></div></div></Shell>;
  if (!payload) return <Shell><div className="tp-wrap"><div className="tp-hero"><p className="tp-muted">{T.r_loading}</p></div></div></Shell>;

  const { student, result, band_message, disclaimer, reward, booking, consult_duration, topic } = payload;
  const name = student.name.split(" ")[0].toUpperCase();
  const shareUrl = `https://wa.me/?text=${encodeURIComponent(T.r_share_text(result.score, result.max_score, result.correct) + `${window.location.origin}/physics-test`)}`;

  return (
    <Shell>
      <div className="tp-wrap" style={{ maxWidth: 880 }}>
        <section className="tp-score-hero">
          <div className="tp-eyebrow">{T.r_eyebrow}</div>
          <h1 data-testid="tp-congrats">{T.r_congrats}, {name}!</h1>
          <p className="tp-muted">{T.r_done}</p>
          <div className="tp-band" style={{ marginBottom: 14 }} data-testid="tp-result-topic">{T.r_result_of(topic?.name || "General")}</div>
          <div className="tp-score-big" data-testid="tp-score">{result.score}/{result.max_score}</div>
          <p className="tp-sub" data-testid="tp-scored-in">{T.r_scored_in(result.score, result.max_score, topic?.name || "General")}</p>
          <p className="tp-note">{T.r_review_note}</p>
        </section>
        <div className="tp-score-grid" data-testid="tp-score-grid">
          <div className="tp-stat"><b>{result.correct}/5</b><span>{T.r_correct}</span></div>
          <div className="tp-stat"><b>{result.incorrect}/5</b><span>{T.r_incorrect}</span></div>
          <div className="tp-stat"><b>{result.unanswered}/5</b><span>{T.r_unans}</span></div>
          <div className="tp-stat"><b>{result.accuracy}%</b><span>{T.r_acc}</span></div>
          <div className="tp-stat"><b>{fmtTime(result.time_taken_sec, T.r_min)}</b><span>{T.r_time}</span></div>
        </div>
        <div className="tp-band" data-testid="tp-band">{band_message}</div>
        <p className="tp-note" style={{ textAlign: "center" }}>{disclaimer}</p>

        <div style={{ textAlign: "center", marginBottom: 28 }}>
          <a className="tp-btn secondary" href={shareUrl} target="_blank" rel="noreferrer" data-testid="tp-share-whatsapp"><MessageCircle size={16} /> {T.r_share}</a>
        </div>

        {reward ? (
          <div className="tp-reward" data-testid="tp-reward">
            <div style={{ display: "flex", justifyContent: "center" }}><IndianRupee size={30} /></div>
            <h2>{T.r_reward_title}</h2>
            <p>{T.r_reward_pre}</p>
            <div className="amount" data-testid="tp-reward-amount">₹{reward.amount}</div>
            <p>{T.r_reward_post}</p>
          </div>
        ) : (
          <div className="tp-keeplearning" data-testid="tp-keeplearning">
            <h2 className="tp-h2">{T.r_keep_title}</h2>
            <p className="tp-muted">{T.r_keep_body}</p>
          </div>
        )}

        {reward && <>
          <div className="tp-panel" style={{ marginBottom: 26 }}>
            <h2 className="tp-h2" style={{ color: "#f77f00" }}>{T.r_waiting}</h2>
            <div className="tp-steps" style={{ margin: "16px 0", maxWidth: "100%" }}>
              <div style={{ background: "#fff3e3", color: "#b54708" }}>{T.r_step1} ({consult_duration} {T.r_min}).</div>
              <div style={{ background: "#fff3e3", color: "#b54708" }}>{T.r_step2}</div>
              <div style={{ background: "#fff3e3", color: "#b54708" }}>{T.r_step3}</div>
            </div>
            <Tracker reward={reward} booking={booking} labels={T.r_track} />
            <p className="tp-pill info" data-testid="tp-payment-status">{T.pay_messages[reward.status] || reward.status}</p>
            <div style={{ marginTop: 20 }}><button className="tp-btn" onClick={() => { track("consult_click"); navigate(`/physics-test/book/${student.student_id}`); }} data-testid="tp-book-cta">{T.r_book} <ArrowRight size={16} /></button></div>
          </div>
        </>}
        {!reward && <div style={{ textAlign: "center", marginBottom: 30 }}><button className="tp-btn secondary" onClick={() => { track("consult_click"); navigate(`/physics-test/book/${student.student_id}`); }} data-testid="tp-book-cta">{T.r_book_free} <ArrowRight size={16} /></button></div>}

        <section style={{ paddingBottom: 40 }}>
          <h2 className="tp-h2">{T.r_analysis_title}</h2>
          <p className="tp-muted">{T.r_analysis_sub}</p>
          <div className="tp-analysis" data-testid="tp-analysis">
            {result.analysis.map((q, i) => (
              <div className="tp-analysis-card" key={q.question_id} data-testid={`tp-analysis-${i}`}>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                  <span className="tp-pill info">Q{i + 1}{q.topic ? ` · ${q.topic.toUpperCase()}` : ""}</span>
                  {q.difficulty && <span className="tp-pill warn">{q.difficulty.toUpperCase()}</span>}
                  <span className={`tp-pill ${q.status === "correct" ? "ok" : q.status === "incorrect" ? "bad" : ""}`}>{q.status === "correct" ? T.st_correct : q.status === "incorrect" ? T.st_incorrect : T.st_na}</span>
                </div>
                <h4>{q.text}</h4>
                {q.image_url && <img src={q.image_url} alt="Question diagram" />}
                <div className="tp-analysis-options">
                  {["A", "B", "C", "D"].map((key) => <div key={key} className={key === q.correct_answer ? "correct" : key === q.selected ? "picked-wrong" : ""}><b>{key}.</b> {q.options[key]}</div>)}
                </div>
                <p className="tp-muted" style={{ fontSize: 12 }}>
                  {T.r_your} <b>{q.selected ? `${T.r_option} ${q.selected}` : T.r_not_attempted}</b> · {T.r_correct_ans} <b>{T.r_option} {q.correct_answer}</b>
                </p>
                {q.explanation && <div className="tp-expl" data-testid={`tp-explanation-${i}`}><b>{T.r_expl}</b><br />{q.explanation}</div>}
                {q.solution && <div className="tp-expl"><b>{T.r_sol}</b><br />{q.solution}</div>}
              </div>
            ))}
          </div>
          <p className="tp-note" style={{ marginTop: 18, textAlign: "center" }}>{payload.tabs_notice}</p>
          <div style={{ textAlign: "center", marginTop: 10, display: "flex", gap: 14, justifyContent: "center", flexWrap: "wrap" }}>
            <a className="tp-back" href={`/physics-test/status/${student.student_id}`} data-testid="tp-status-link">{T.r_status_link}</a>
            <a className="tp-back" href="/physics-test/topics" data-testid="tp-explore-topics">{T.r_explore} →</a>
          </div>
        </section>
      </div>
    </Shell>
  );
}
